import { useEffect } from 'react'
import { Card, EmptyState, Skeleton } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useChangelog } from '../api/hooks'
import { Markdown } from '../lib/markdown'
import { useChangelogStore } from '../stores/changelog'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** CalVer 2026.09.24[.N] -> "24 Sep 2026" (+ " · release N" for a same-day
 *  re-release). Fixed month names: ICU's en-GB short month varies ("Sept"). */
function releaseLabel(version: string): string {
  const m = /^(\d{4})\.(\d{2})\.(\d{2})(?:\.(\d+))?$/.exec(version)
  const month = m ? MONTHS[Number(m[2]) - 1] : undefined
  if (!m || !month) return version
  const day = `${Number(m[3])} ${month} ${m[1]}`
  return m[4] ? `${day} · release ${m[4]}` : day
}

/** Release notes from changelog/ (GET /changelog). Visiting marks the newest
 *  entry seen, which clears the sidebar's unread dot. */
export default function WhatsNewPage() {
  const changelog = useChangelog()
  const markSeen = useChangelogStore((s) => s.markSeen)

  const entries = changelog.data?.entries ?? []
  const newest = entries[0]?.version
  useEffect(() => {
    if (newest) markSeen(newest)
  }, [newest, markSeen])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Release notes" title="What's new">
        {changelog.data && (
          <p className="mt-1 font-mono text-xs text-ostrel-ink-muted">Running {changelog.data.version}</p>
        )}
      </PageHeader>

      {changelog.isLoading && <Skeleton className="h-40" />}
      {changelog.isError && <EmptyState title="Couldn't load release notes" hint="Try again in a moment." />}
      {changelog.data && entries.length === 0 && <EmptyState title="No release notes yet" />}

      {entries.map((entry) => (
        <Card key={entry.version} title={releaseLabel(entry.version)}>
          <Markdown source={entry.body} />
          <p className="mt-3 font-mono text-[10px] text-ostrel-ink-muted">{entry.version}</p>
        </Card>
      ))}
    </div>
  )
}
